import {
    isValidAccountSubtype,
    isValidAccountType,
} from "@common/constants/domains/accounting/accountTypeHelpers";
import { Account } from "@common/types/domains/accounting/account";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { format, isSameDay, parseISO, subDays } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import {
    monetaryValuesEqual,
    subtractMonetaryValues,
    sumMonetaryValues,
} from "@common/helpers/monetary";
import {
    AccountProxyObject,
    BalanceSheetImportStep,
    BalanceSheetRowAction,
} from "./types";

export interface AccountFieldErrors {
    name?: string;
    accountClass?: string;
    balance?: string;
    mappedKickAccountId?: string;
}

export interface ValidationErrors {
    accounts?: AccountFieldErrors[];
    noAccounts?: string;
    balanceSheet?: string;
    unmappedAccounts?: string;
    balanceSheetDate?: string;
}

interface BalanceSheetTotals {
    assetsCount: number;
    liabilitiesCount: number;
    equityCount: number;
    totalAssets: number;
    totalLiabilities: number;
    totalEquity: number;
    liabilitiesPlusEquity: number;
    difference: number;
}

interface ValidationDisplay {
    balanceSheetTotals?: BalanceSheetTotals;
}

export interface ValidationResult {
    errors: ValidationErrors;
    display: ValidationDisplay;
}

function calculateBalanceSheetTotals(
    accounts: AccountProxyObject[],
): BalanceSheetTotals {
    const accountsWithName = accounts.filter((account) => account.name?.trim());

    const assetAccounts = accountsWithName.filter(
        (a) => a.accountClass === AccountClass.assets,
    );
    const liabilityAccounts = accountsWithName.filter(
        (a) => a.accountClass === AccountClass.liabilities,
    );
    const equityAccounts = accountsWithName.filter(
        (a) => a.accountClass === AccountClass.equity,
    );

    const totalAssets = sumMonetaryValues(
        assetAccounts.map((a) => a.balance ?? 0),
    );
    const totalLiabilities = sumMonetaryValues(
        liabilityAccounts.map((a) => a.balance ?? 0),
    );
    const totalEquity = sumMonetaryValues(
        equityAccounts.map((a) => a.balance ?? 0),
    );
    const liabilitiesPlusEquity = sumMonetaryValues([
        totalLiabilities,
        totalEquity,
    ]);
    const difference = subtractMonetaryValues(
        totalAssets,
        liabilitiesPlusEquity,
    );

    return {
        assetsCount: assetAccounts.length,
        liabilitiesCount: liabilityAccounts.length,
        equityCount: equityAccounts.length,
        totalAssets,
        totalLiabilities,
        totalEquity,
        liabilitiesPlusEquity,
        difference,
    };
}

function validateAccountFields(
    accounts: AccountProxyObject[],
): AccountFieldErrors[] {
    return accounts.map((account) => {
        if (!account.name?.trim()) {
            return {};
        }

        const errors: AccountFieldErrors = {};

        if (account.accountClass === null) {
            errors.accountClass = "Missing classification";
        }

        if (account.balance === null) {
            errors.balance = "Missing balance";
        }

        return errors;
    });
}

interface ValidateBalanceSheetOptions {
    accounts: AccountProxyObject[];
    step: BalanceSheetImportStep;
    extractedDate?: string | null; // ISO string
    bookkeepingStartDate?: string | null; // ISO string
}

export function validateBalanceSheetAccounts(
    options: ValidateBalanceSheetOptions,
): ValidationResult {
    const { accounts, step, extractedDate, bookkeepingStartDate } = options;
    const accountsWithName = accounts.filter((account) => account.name?.trim());

    // Early return if no accounts
    if (accountsWithName.length === 0) {
        return {
            errors: { noAccounts: "Add at least one account" },
            display: {},
        };
    }

    // Compute balance sheet totals for display
    const balanceSheetTotals = calculateBalanceSheetTotals(accounts);

    const result: ValidationResult = {
        errors: {},
        display: { balanceSheetTotals },
    };

    // Validate account fields
    const accountErrors = validateAccountFields(accounts);
    const hasEnterBalancesStepError = accountErrors.some(
        (error) => !!error.balance || !!error.accountClass,
    );
    const hasAccountError =
        step === BalanceSheetImportStep.EnterBalances
            ? hasEnterBalancesStepError
            : false;

    if (hasAccountError) {
        result.errors.accounts = accountErrors;
    }

    // Check balance sheet equation only in EnterBalances step
    if (step === BalanceSheetImportStep.EnterBalances) {
        if (!monetaryValuesEqual(balanceSheetTotals.difference, 0)) {
            result.errors.balanceSheet =
                "Balance sheet must balance before proceeding (Assets = Liabilities + Equity)";
        }
    }

    // Validate balance sheet date (always validate, regardless of step)
    if (!extractedDate) {
        result.errors.balanceSheetDate = "Please select a balance sheet date";
    } else if (bookkeepingStartDate) {
        // Calculate one day before bookkeeping start (as ISO string)
        const bookkeepingStartDateObj = parseISO(bookkeepingStartDate);
        const oneDayBeforeObj = subDays(bookkeepingStartDateObj, 1);
        const extractedDateObj = parseISO(extractedDate);
        const isDateValid = isSameDay(extractedDateObj, oneDayBeforeObj);

        if (!isDateValid) {
            result.errors.balanceSheetDate = `Date must be ${format(oneDayBeforeObj, fullDateFullYearFormat)} (one day before bookkeeping start date)`;
        }
    }

    // Check for unmapped accounts when moving to MapAccounts step
    if (step === BalanceSheetImportStep.MapAccounts) {
        const unmappedAccounts = accountsWithName.filter(
            (account) => !account.mappedKickAccountId,
        );

        if (unmappedAccounts.length > 0) {
            result.errors.unmappedAccounts =
                "All accounts must be mapped before creating journal entry";
        }
    }

    return result;
}

interface ValidateAndTransformAccountsParams {
    newData: Array<Record<string, any>>;
    prevData: AccountProxyObject[];
    existingAccounts: Account[];
}

function normalizeAccountData(
    data: Array<Record<string, any>>,
): AccountProxyObject[] {
    return data.map((row): AccountProxyObject => {
        const id = typeof row.id === "string" ? row.id : String(row.id ?? "");
        const name =
            row.name !== undefined && row.name !== null
                ? String(row.name).trim()
                : "";
        const accountClass =
            row.accountClass && typeof row.accountClass === "string"
                ? (row.accountClass as AccountClass)
                : null;
        const balance =
            row.balance !== undefined && row.balance !== null
                ? Number(row.balance)
                : null;
        const mappedKickAccountId =
            row.mappedKickAccountId &&
            typeof row.mappedKickAccountId === "string"
                ? row.mappedKickAccountId
                : null;

        // Validate account type - but not against class or subtype, that will be done in create account modal
        const accountType =
            row.accountType && isValidAccountType(row.accountType)
                ? row.accountType
                : null;

        // Validate account subtype - but not against class or type, that will be done in create account modal
        const accountSubtype =
            row.accountSubtype && isValidAccountSubtype(row.accountSubtype)
                ? row.accountSubtype
                : null;

        // Preserve action and isActionAccepted from raw data, with defaults
        const action =
            row.action &&
            Object.values(BalanceSheetRowAction).includes(row.action)
                ? row.action
                : BalanceSheetRowAction.CreateAccount;
        const isActionAccepted =
            typeof row.isActionAccepted === "boolean"
                ? row.isActionAccepted
                : false;

        return {
            id,
            name,
            accountClass,
            balance,
            accountType,
            accountSubtype,
            mappedKickAccountId,
            action,
            isActionAccepted,
        };
    });
}

export function validateAndTransformAccounts(
    params: ValidateAndTransformAccountsParams,
): AccountProxyObject[] {
    const { newData, prevData, existingAccounts } = params;
    // First, normalize the raw data into proper AccountProxyObject format
    const normalizedData = normalizeAccountData(newData);

    const correctedData = normalizedData.map((row, idx) => {
        const prevRow = prevData[idx] || {
            id: "",
            name: "",
            accountClass: null,
            balance: null,
            accountType: null,
            accountSubtype: null,
            mappedKickAccountId: null,
            action: BalanceSheetRowAction.CreateAccount,
            isActionAccepted: false,
        };

        const isNameChanged = row.name !== prevRow.name;
        const isClassChanged = row.accountClass !== prevRow.accountClass;
        const isTypeChanged = row.accountType !== prevRow.accountType;
        const isSubtypeChanged = row.accountSubtype !== prevRow.accountSubtype;
        const isMappedIdChanged =
            row.mappedKickAccountId !== prevRow.mappedKickAccountId;

        if (prevRow.isActionAccepted && isMappedIdChanged) {
            row.isActionAccepted = false;
        }

        if (isNameChanged || isClassChanged) {
            // Erase previous mapping
            if (!isTypeChanged) {
                row.accountType = null;
            }
            if (!isSubtypeChanged) {
                row.accountSubtype = null;
            }
            if (!isMappedIdChanged) {
                row.mappedKickAccountId = null;
            }

            // Auto-select mapped account if exact match exists
            if (row.name && row.accountClass) {
                const exactMatch = existingAccounts.find(
                    (account) =>
                        account.name.toLowerCase() ===
                            row.name.toLowerCase().trim() &&
                        account.class ===
                            (row.accountClass as unknown as AccountClass),
                );
                if (exactMatch) {
                    row.mappedKickAccountId = exactMatch.id;
                }
            }
        }

        // Invalidate mapped account
        if (row.mappedKickAccountId && isMappedIdChanged) {
            const mappedAccount = existingAccounts.find(
                (a) => a.id === row.mappedKickAccountId,
            );
            if (
                !mappedAccount ||
                (row.accountClass && mappedAccount.class !== row.accountClass)
            ) {
                row.mappedKickAccountId = null;
            }
        }

        return row;
    });

    return correctedData;
}
